import { useEffect, useMemo, useState } from 'react';
import { Api } from '../application/panelApi';
import { LaneIcon } from '../components/LaneIcon';
import { Modal } from '../components/Modal';
import { modal } from '../components/ModalHost';
import { toast } from '../components/ToastHost';
import { BulkDeleteTab } from '../features/profiles/BulkDeleteTab';
import { OverviewTab } from '../features/profiles/OverviewTab';
import { TemplateTab } from '../features/profiles/TemplateTab';
import { liveClusters } from '../features/profiles/shared';
import { useProfileTools } from '../features/profiles/useProfileTools';
import {
  overviewTotals,
  useProfilesOverview,
  type OverviewRow,
} from '../features/profiles/useProfilesOverview';
import { takePendingProfileCluster } from '../features/clusters';
import { nf, plural, sizeStr } from '../lib/format';
import { useApp } from '../store/AppContext';

const noop = () => undefined;

function csvCell(v: string | number): string {
  const s = String(v);
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}


function profilesCsv(rows: OverviewRow[]): string {
  const head = ['Кластер', 'Всего', 'Активных', 'Неактивных', 'Диск МБ'];
  const body = rows.map(({ c, prof }) => [
    c.name || c.id,
    prof.total ?? 0,
    prof.active ?? 0,
    prof.idle ?? 0,
    prof.diskMb ?? 0,
  ]);
  return [head, ...body].map((r) => r.map(csvCell).join(';')).join('\n');
}

function CsvExportBody({ csv }: { csv: string }) {
  return <textarea id="csvOut" rows={10} readOnly value={csv} />;
}

function openCsvExport(name: string, csv: string): void {
  modal(
    `Экспорт · ${name}`,
    <CsvExportBody csv={csv} />,
    <>
      <button type="button" className="btn" data-close>
        Закрыть
      </button>
      <button
        type="button"
        className="btn btn-primary"
        id="csvCopy"
        onClick={() => {
          void (async () => {
            try {
              await navigator.clipboard.writeText(csv);
              toast('Таблица скопирована. Вставьте её в Excel или Google Sheets.', 'ok');
            } catch {
              document.querySelector<HTMLTextAreaElement>('#csvOut')?.select();
              toast(
                'Браузер не дал доступ к буферу обмена. Текст уже выделен — нажмите Ctrl+C, чтобы скопировать его.',
                'err',
              );
            }
          })();
        }}
      >
        Скопировать
      </button>
    </>,
    true,
  );
}

export function ProfilesPage() {
  const { controlMode, refreshClusters } = useApp();
  const control = controlMode === 'control';

  const [sheet, setSheet] = useState<'template' | 'delete' | null>(null);
  const [cid, setCid] = useState(() => takePendingProfileCluster() ?? 'all');

  const tools = useProfileTools(control);
  const { rows, version, clusters } = useProfilesOverview('all');
  const live = useMemo(() => liveClusters(clusters), [clusters]);
  const totals = control && rows?.length ? overviewTotals(rows) : null;
  const noDisk = !rows?.length || rows.every(({ prof }) => prof.diskMb == null);


  const deploying = clusters.some((c) => c.status === 'deploying');
  useEffect(() => {
    if (!control) return undefined;
    void refreshClusters().catch(noop);
    const tick = async () => {
      if (document.visibilityState === 'hidden') return;
      if (deploying) await Api.clusters.pingAll().catch(noop);
      await refreshClusters().catch(noop);
    };
    const timer = window.setInterval(() => void tick(), deploying ? 2500 : 8000);
    return () => window.clearInterval(timer);
  }, [control, deploying, refreshClusters]);

  return (
    <section id="page-profiles" className="sv">
      <div className="sv-head">
        <div className="sv-title">
          <h1 className="h1-lane">
            <LaneIcon kind="profiles" />
            Профили
          </h1>
          <p className="sv-facts">
            <span id="prTotal">
              <b>{nf.format(totals?.total ?? 0)}</b>{' '}
              {plural(totals?.total ?? 0, 'профиль', 'профиля', 'профилей')} на {rows?.length ?? 0}{' '}
              {plural(rows?.length ?? 0, 'сервере', 'серверах', 'серверах')}
            </span>
            <span id="prAct">
              <b>{nf.format(totals?.active ?? 0)}</b> с открытым браузером
            </span>
            <span id="prIdle">
              <b>{nf.format(totals?.ready ?? 0)}</b> готовы
              {totals?.total ? ` (${Math.round((totals.ready / totals.total) * 100)}%)` : ''}
            </span>
            <span id="prDisk">
              диск{' '}
              {totals && !noDisk ? (
                <>
                  <b>{sizeStr(totals.disk)}</b> из {sizeStr(totals.diskTotal)}
                </>
              ) : (
                '—'
              )}
            </span>
          </p>
        </div>
        <div className="actions">
          <button
            type="button"
            className="btn"
            id="prTemplateBtn"
            onClick={() => setSheet('template')}
          >
            Шаблон новых профилей
          </button>
          <button
            type="button"
            className="btn"
            id="prCleanupBtn"
            onClick={() => setSheet('delete')}
          >
            Очистка
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => openCsvExport('profiles.csv', profilesCsv(rows ?? []))}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v11m-4-4 4 4 4-4M4 16v4h16v-4" /></svg>
            Экспорт CSV
          </button>
        </div>
      </div>

      {sheet === 'template' ? (
        <Modal title="Шаблон новых профилей" wide onClose={() => setSheet(null)}>
          <TemplateTab active tools={tools} />
        </Modal>
      ) : null}
      {sheet === 'delete' ? (
        <Modal title="Очистка профилей" wide onClose={() => setSheet(null)}>
          <BulkDeleteTab active clusters={live} engineChromium={tools.engineChromium} />
        </Modal>
      ) : null}
      <OverviewTab
        active
        cid={cid}
        setCid={setCid}
        clusters={clusters}
        rows={rows}
        version={version}
        control={control}
        engineChromium={tools.engineChromium}
      />
    </section>
  );
}
